import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import ResponsiveDetailPanel from '@/components/dashboard/ResponsiveDetailPanel';
import { useIsWideScreen } from '@/hooks/use-media-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Trash2,
  HardHat,
  ClipboardList,
  StickyNote,
  Receipt,
  FileDiff,
  LayoutDashboard,
  Loader2,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import JobOverviewTab from '@/components/jobs/tabs/JobOverviewTab';
import ChecklistPanel from '@/components/checklist/ChecklistPanel';
import JobPhasesTab from '@/components/jobs/tabs/JobPhasesTab';
import JobChangeOrdersTab from '@/components/jobs/tabs/JobChangeOrdersTab';
import JobNotesTab from '@/components/jobs/tabs/JobNotesTab';
import JobInvoicesTab from '@/components/jobs/tabs/JobInvoicesTab';
import { deleteJob, isJobArchived, markJobComplete, restoreJob } from '@/lib/jobsData';
import { statusLabel } from '@/lib/statusLabels';
import { toast } from 'sonner';

const statusBadgeStyles = {
  active:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  closed: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
};

export const JobDetailSheet = ({
  job,
  clients = [],
  open,
  onOpenChange,
  onUpdated,
  onDeleted,
  panelClassName,
}) => {
  const { t } = useTranslation();
  const isWide = useIsWideScreen();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('overview');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  if (!job) return null;

  const archived = isJobArchived(job);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteJob(job.id);
      toast.success(t('jobs.detail.deleted_toast', { name: job.name }));
      setConfirmDelete(false);
      onOpenChange?.(false);
      onDeleted?.(job);
    } catch (e) {
      toast.error(e?.message || t('jobs.detail.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      const updated = await markJobComplete(job);
      toast.success(t('jobs.detail.completed_toast', { name: job.name }));
      setConfirmComplete(false);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || t('jobs.detail.complete_failed'));
    } finally {
      setCompleting(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const updated = await restoreJob(job);
      toast.success(t('jobs.detail.restored_toast', { name: job.name }));
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || t('jobs.detail.restore_failed'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <ResponsiveDetailPanel
      open={open}
      onOpenChange={onOpenChange}
      className={panelClassName}
      testId="job-detail-sheet"
      sheetClassName="sm:max-w-xl md:max-w-2xl sm:w-[600px]"
    >
        <SheetHeader className="px-5 sm:px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <HardHat size={14} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {t('jobs.detail.eyebrow')}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {isWide ? (
                <h2
                  className="text-foreground tracking-tight text-lg sm:text-xl truncate"
                  data-testid="job-detail-title"
                >
                  {job.name}
                </h2>
              ) : (
                <SheetTitle
                  className="text-foreground tracking-tight text-lg sm:text-xl truncate"
                  data-testid="job-detail-title"
                >
                  {job.name}
                </SheetTitle>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn(
                    'uppercase text-[10px] tracking-[0.16em]',
                    statusBadgeStyles[job.status] || statusBadgeStyles.active
                  )}
                >
                  {statusLabel(t, job.status || 'active')}
                </Badge>
                <span
                  className="text-xs text-muted-foreground truncate"
                  data-testid="job-detail-client"
                >
                  {job.client}
                </span>
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-1">
              {archived ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRestore}
                  disabled={restoring}
                  className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px] h-8"
                  data-testid="job-detail-restore"
                >
                  {restoring ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCcw size={12} className="mr-1.5" />
                  )}
                  {t('jobs.detail.restore')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmComplete(true)}
                  disabled={completing}
                  className="border-[hsl(var(--success))]/40 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10 uppercase tracking-[0.16em] text-[10px] h-8"
                  data-testid="job-detail-mark-complete"
                >
                  {completing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 size={12} className="mr-1.5" />
                  )}
                  {t('jobs.detail.mark_complete')}
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
                className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-8 w-8"
                data-testid="job-detail-delete"
                aria-label={t('jobs.detail.delete_aria')}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
          {!isWide ? (
            <SheetDescription className="sr-only">
              {t('jobs.detail.description')}
            </SheetDescription>
          ) : null}
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-5 sm:px-6 pt-3 pb-2 border-b border-border">
            <TabsList
              className="bg-[hsl(var(--muted))] border border-border h-9 p-1 w-full justify-start overflow-x-auto"
              data-testid="job-detail-tabs"
            >
              <TabsTrigger
                value="overview"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-[11px]"
                data-testid="job-tab-overview"
              >
                <LayoutDashboard size={13} className="mr-1.5" />
                {t('jobs.detail.tab_overview')}
              </TabsTrigger>
              <TabsTrigger
                value="phases"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-[11px]"
                data-testid="job-tab-phases"
              >
                <ClipboardList size={13} className="mr-1.5" />
                {t('jobs.detail.tab_phases')}
              </TabsTrigger>
              <TabsTrigger
                value="change_orders"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-[11px]"
                data-testid="job-tab-change-orders"
              >
                <FileDiff size={13} className="mr-1.5" />
                {t('jobs.detail.tab_change_orders', { defaultValue: 'Change Orders' })}
              </TabsTrigger>
              <TabsTrigger
                value="notes"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-[11px]"
                data-testid="job-tab-notes"
              >
                <StickyNote size={13} className="mr-1.5" />
                {t('jobs.detail.tab_notes')}
              </TabsTrigger>
              <TabsTrigger
                value="invoices"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-[11px]"
                data-testid="job-tab-invoices"
              >
                <Receipt size={13} className="mr-1.5" />
                {t('jobs.detail.tab_invoices')}
              </TabsTrigger>
            </TabsList>
          </div>
          <ScrollArea className="flex-1 kc-scroll">
            <div className="px-5 sm:px-6 py-4">
              <TabsContent value="overview" className="mt-0 focus-visible:outline-none">
                <JobOverviewTab job={job} clients={clients} />
                {isAdmin ? <ChecklistPanel jobId={job.id} className="mt-4" /> : null}
              </TabsContent>
              <TabsContent value="phases" className="mt-0 focus-visible:outline-none">
                <JobPhasesTab job={job} onUpdated={onUpdated} />
              </TabsContent>
              <TabsContent value="change_orders" className="mt-0 focus-visible:outline-none">
                <JobChangeOrdersTab job={job} onUpdated={onUpdated} />
              </TabsContent>
              <TabsContent value="notes" className="mt-0 focus-visible:outline-none">
                <JobNotesTab job={job} onUpdated={onUpdated} />
              </TabsContent>
              <TabsContent value="invoices" className="mt-0 focus-visible:outline-none">
                <JobInvoicesTab job={job} onUpdated={onUpdated} />
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
          <AlertDialogContent
            className="bg-[hsl(var(--card))] border-border"
            data-testid="job-complete-confirm-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground">
                {t('jobs.detail.complete_confirm_title', { name: job.name })}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                {t('jobs.detail.complete_confirm_description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
                disabled={completing}
              >
                {t('actions.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleMarkComplete}
                disabled={completing}
                className="bg-[hsl(var(--success))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--success))]/90"
                data-testid="job-complete-confirm-action"
              >
                {completing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 size={14} className="mr-2" />
                )}
                {t('jobs.detail.mark_complete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent
            className="bg-[hsl(var(--card))] border-border"
            data-testid="job-delete-confirm-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground">
                {t('jobs.detail.delete_confirm_title', { name: job.name })}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                {t('jobs.detail.delete_confirm_description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
                disabled={deleting}
              >
                {t('actions.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
                data-testid="job-delete-confirm-action"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('actions.deleting')}
                  </>
                ) : (
                  <>{t('jobs.detail.delete_aria')}</>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </ResponsiveDetailPanel>
  );
};

export default JobDetailSheet;
